import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/admin/api";
import PageHeader, { Btn } from "@/admin/components/PageHeader";
import { toast } from "sonner";
import { Plus, Pencil, Trash, PaperPlaneTilt, Clock, Eye, ArrowClockwise } from "@phosphor-icons/react";

const STATUS_STYLES = {
  draft: "bg-zinc-100 text-zinc-700",
  scheduled: "bg-amber-100 text-amber-800",
  sending: "bg-blue-100 text-blue-800",
  sent: "bg-emerald-100 text-emerald-800",
  partial: "bg-orange-100 text-orange-800",
  failed: "bg-red-100 text-red-800",
};

export default function CampaignsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/campaigns", { params: filter ? { status: filter } : {} });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const del = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    await adminApi.delete(`/campaigns/${id}`);
    toast.success("Deleted");
    load();
  };

  const sendNow = async (id) => {
    if (!window.confirm("Send this campaign now to all matched recipients?")) return;
    toast.loading("Sending…", { id: "send" });
    try {
      const { data } = await adminApi.post(`/campaigns/${id}/send`);
      if (data.error) {
        toast.error(data.error, { id: "send" });
      } else {
        toast.success(`Sent ${data.sent || 0} / ${data.total || 0} (failed ${data.failed || 0})`, { id: "send" });
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message, { id: "send" });
    }
  };

  const schedule = async (c) => {
    const dflt = new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16);
    const raw = prompt(`Schedule ${c.name} at (UTC datetime, YYYY-MM-DDTHH:mm)`, dflt);
    if (!raw) return;
    try {
      const scheduled_at = new Date(raw).toISOString();
      await adminApi.post(`/campaigns/${c.id}/schedule`, { scheduled_at });
      toast.success("Scheduled");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="admin-campaigns-page">
      <PageHeader
        eyebrow="Email · Campaigns"
        title="Campaigns"
        description="Draft, preview, schedule & send HTML email campaigns to your leads."
        actions={
          <>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-zinc-300 p-2 text-sm bg-white"
              data-testid="campaigns-filter"
            >
              <option value="">All statuses</option>
              {Object.keys(STATUS_STYLES).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Btn onClick={() => setEditing({ name: "", subject: "", html_body: "", filters: { limit: 100 }, lead_ids: [] })} data-testid="campaign-new">
              <Plus size={14} weight="bold" /> New campaign
            </Btn>
          </>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                <th className="p-3">Campaign</th>
                <th className="p-3">Subject</th>
                <th className="p-3">Status</th>
                <th className="p-3">Stats</th>
                <th className="p-3">Scheduled</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-zinc-50" data-testid={`campaign-row-${c.id}`}>
                  <td className="p-3 font-medium text-zinc-900">{c.name}</td>
                  <td className="p-3 text-zinc-700 max-w-xs truncate">{c.subject}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_STYLES[c.status] || "bg-zinc-100"}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="p-3 text-[11px] font-mono text-zinc-600">
                    {c.stats ? `${c.stats.sent}/${c.stats.total} · ${c.stats.failed} fail` : "—"}
                  </td>
                  <td className="p-3 text-[11px] font-mono text-zinc-500">
                    {c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing(c)} title="Edit" className="p-1 text-zinc-500 hover:text-zinc-900"><Pencil size={16} /></button>
                    <button onClick={() => schedule(c)} title="Schedule" className="p-1 text-zinc-500 hover:text-amber-600 ml-1"><Clock size={16} /></button>
                    <button onClick={() => sendNow(c.id)} title="Send now" className="p-1 text-zinc-500 hover:text-[#0044FF] ml-1" data-testid={`campaign-send-${c.id}`}><PaperPlaneTilt size={16} /></button>
                    <button onClick={() => del(c.id)} title="Delete" className="p-1 text-zinc-400 hover:text-red-600 ml-1"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr><td colSpan={6} className="p-10 text-center text-zinc-500 text-sm">No campaigns yet.</td></tr>
              )}
            </tbody>
          </table>
          <div className="p-3 border-t border-zinc-200 flex justify-end">
            <button onClick={load} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowClockwise size={12} /> Refresh</button>
          </div>
        </div>
      </div>

      {editing && (
        <CampaignEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CampaignEditor({ initial, onClose, onSaved }) {
  const [c, setC] = useState({
    id: initial.id,
    name: initial.name || "",
    subject: initial.subject || "",
    html_body: initial.html_body || "",
    filters: initial.filters || { limit: 100 },
    lead_ids: initial.lead_ids || [],
    ad_hoc_emails: initial.ad_hoc_emails || [],
    template_id: initial.template_id || null,
    scheduled_at: initial.scheduled_at || null,
  });
  const [adHocText, setAdHocText] = useState((initial.ad_hoc_emails || []).join("\n"));
  const [templates, setTemplates] = useState([]);
  const [recipientsEstimate, setRecipientsEstimate] = useState(null);
  const [states, setStates] = useState([]);

  useEffect(() => {
    adminApi.get("/templates").then((r) => setTemplates(r.data.items || [])).catch(() => {});
    adminApi.get("/leads", { params: { limit: 1 } }).then((r) => setStates(r.data.states || [])).catch(() => {});
  }, []);

  // Parse ad-hoc emails on change
  const parsedAdHoc = adHocText
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter((s) => s && /@/.test(s));

  const applyTemplate = (id) => {
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    setC((x) => ({ ...x, template_id: id, subject: tpl.subject, html_body: tpl.html_body, name: x.name || tpl.name }));
  };

  const save = async () => {
    const payload = {
      name: c.name,
      subject: c.subject,
      html_body: c.html_body,
      template_id: c.template_id,
      filters: c.filters,
      lead_ids: c.lead_ids,
      ad_hoc_emails: parsedAdHoc,
      scheduled_at: c.scheduled_at,
    };
    try {
      let id = c.id;
      if (id) {
        await adminApi.put(`/campaigns/${id}`, payload);
      } else {
        const { data } = await adminApi.post("/campaigns", payload);
        id = data.id;
      }
      toast.success("Saved");
      onSaved(id);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const estimate = async () => {
    if (!c.id) {
      toast.message("Save the campaign first to estimate recipients.");
      return;
    }
    const { data } = await adminApi.post(`/campaigns/${c.id}/recipients/estimate`);
    setRecipientsEstimate(data);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-900 max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500">
            {c.id ? "Edit campaign" : "New campaign"}
          </div>
          <button onClick={onClose} className="text-sm text-zinc-500 hover:text-zinc-900">Close ✕</button>
        </div>
        <div className="grid md:grid-cols-2 flex-1 overflow-hidden">
          <div className="p-6 space-y-4 overflow-y-auto border-r border-zinc-200">
            <Row label="Campaign name">
              <input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} className="input" data-testid="cmp-name" />
            </Row>
            <Row label="Start from template">
              <select value={c.template_id || ""} onChange={(e) => applyTemplate(e.target.value)} className="input">
                <option value="">— none —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Row>
            <Row label="Subject">
              <input value={c.subject} onChange={(e) => setC({ ...c, subject: e.target.value })} className="input" data-testid="cmp-subject" />
            </Row>
            <Row label="HTML body">
              <textarea
                value={c.html_body}
                onChange={(e) => setC({ ...c, html_body: e.target.value })}
                rows={14}
                className="w-full border border-zinc-300 p-3 text-xs font-mono focus:outline-none focus:border-[#0044FF]"
                data-testid="cmp-html"
              />
            </Row>
            <div className="border-t border-zinc-200 pt-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">Audience filters</div>
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="State" value={c.filters?.state || ""} onChange={(v) => setC({ ...c, filters: { ...c.filters, state: v || null } })} options={["", ...states]} />
                <SelectField label="Source" value={c.filters?.source || ""} onChange={(v) => setC({ ...c, filters: { ...c.filters, source: v || null } })} options={["", "yelp", "csv", "mock"]} />
                <SelectField label="Status" value={c.filters?.status || ""} onChange={(v) => setC({ ...c, filters: { ...c.filters, status: v || null } })} options={["", "new", "contacted", "qualified", "rejected"]} />
                <Row label="Recipient limit">
                  <input
                    type="number" min={1} max={5000}
                    value={c.filters?.limit || 100}
                    onChange={(e) => setC({ ...c, filters: { ...c.filters, limit: Number(e.target.value) || 100 } })}
                    className="input"
                  />
                </Row>
              </div>
              <button onClick={estimate} className="mt-3 text-xs text-[#0044FF] hover:underline">Estimate recipients</button>
              {recipientsEstimate && (
                <div className="mt-2 text-xs text-zinc-600">
                  <strong>{recipientsEstimate.count}</strong> recipients · sample: {(recipientsEstimate.sample || []).join(", ")}
                </div>
              )}
            </div>
            <div className="border-t border-zinc-200 pt-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">
                Manually add emails (one per line, comma- or semicolon-separated)
              </div>
              <textarea
                value={adHocText}
                onChange={(e) => setAdHocText(e.target.value)}
                rows={4}
                placeholder={"jane@firm.com\nbob@surveyors.com"}
                className="w-full border border-zinc-300 p-2 text-xs font-mono focus:outline-none focus:border-[#0044FF]"
                data-testid="cmp-ad-hoc-emails"
              />
              <div className="mt-1 text-[11px] font-mono text-zinc-500">
                {parsedAdHoc.length} ad-hoc email{parsedAdHoc.length === 1 ? "" : "s"} parsed.
                {parsedAdHoc.length > 0 && (
                  <> Will be merged with filtered leads (set <strong>limit = 0</strong> to send to ad-hoc only).</>
                )}
              </div>
            </div>
          </div>
          <div className="p-6 bg-zinc-50 overflow-y-auto">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">Live preview</div>
            <div className="bg-white border border-zinc-300 p-4">
              <div className="text-xs text-zinc-500 mb-2">Subject: <span className="text-zinc-900">{renderStr(c.subject)}</span></div>
              <iframe title="cmp-preview" className="w-full h-[520px] border border-zinc-200" srcDoc={renderStr(c.html_body)} />
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-zinc-200 flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500">
            Variables: {"{{name}} {{company}} {{city}} {{state}} {{email}}"}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
            <button onClick={save} className="px-4 py-2 bg-[#0044FF] text-white text-sm font-medium" data-testid="cmp-save">Save draft</button>
          </div>
        </div>
        <style>{`.input{width:100%;border:1px solid #d4d4d8;padding:0.5rem;font-size:0.875rem;outline:none}.input:focus{border-color:#0044FF}`}</style>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <Row label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input">
        {options.map((o) => <option key={o} value={o}>{o || "All"}</option>)}
      </select>
    </Row>
  );
}
function renderStr(s) {
  const sample = { name: "Sample Surveyor", city: "Austin", state: "TX", company: "Sample Survey Co", email: "sample@example.com", unsubscribe_url: "#" };
  return Object.entries(sample).reduce((acc, [k, v]) => acc.replaceAll(`{{${k}}}`, v), s || "");
}
